// domain/capacitacion.js  (entidad + mapeos). Sin React, sin Api, sin Swal, sin localStorage/window.
import { toApiDate } from '../../shared/domain/fecha';

// El uuid (y la fecha por defecto) los provee la capa application: el dominio queda determinista.

export const emptyCapacitacion = {
  id: null,
  uuid: '',
  created_at: null,
  nombre_capacitacion: '',
  responsable: '',
  fecha: '',
  id_actividades: [],
  id_municipio: null,
  id_localidad: null,
  id_ciclo: null,
  descripcion: '',
  materiales: '',
  asistentes: [],
  media: [],
};

const toId = value => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return isNaN(n) ? null : n;
};

// Lista de ids numéricos sin duplicados; descarta null/''/NaN.
export const toIds = values => [...new Set((Array.isArray(values) ? values : []).map(toId).filter(id => id !== null))];

export const createCapacitacion = (data = {}) => ({
  ...emptyCapacitacion,
  ...data,
  fecha: toApiDate(data.fecha),
  id_actividades: toIds(data.id_actividades),
  id_municipio: toId(data.id_municipio),
  id_localidad: toId(data.id_localidad),
  id_ciclo: toId(data.id_ciclo),
  descripcion: data.descripcion || '',
  materiales: data.materiales || '',
});

// Actividades del DTO: `actividades` ([{ id }] o ids) > `id_actividades` > `id_actividad` (columna que el backend conserva para la app móvil).
const actividadesDesdeApi = dto => {
  if (Array.isArray(dto.actividades)) {
    return toIds(dto.actividades.map(item => (item !== null && typeof item === 'object' ? item.id : item)));
  }
  if (Array.isArray(dto.id_actividades)) return toIds(dto.id_actividades);
  return toIds([dto.id_actividad]);
};

// DTO del backend -> entidad (asistentes/media solo se mapean; sin UI en este piloto)
export const fromApi = dto =>
  createCapacitacion({
    id: toId(dto.id),
    uuid: dto.uuid,
    created_at: dto.created_at || null,
    nombre_capacitacion: dto.nombre_capacitacion || '',
    responsable: dto.responsable || '',
    fecha: dto.fecha,
    id_actividades: actividadesDesdeApi(dto),
    id_municipio: dto.id_municipio,
    id_localidad: dto.id_localidad,
    id_ciclo: dto.id_ciclo,
    descripcion: dto.descripcion,
    materiales: dto.materiales,
    asistentes: Array.isArray(dto.asistentes) ? dto.asistentes : [],
    media: Array.isArray(dto.media) ? dto.media : [],
  });

const OPTIONAL_FIELDS = ['id_municipio', 'id_localidad', 'id_ciclo', 'descripcion', 'materiales'];
const isEmpty = value => value === null || value === undefined || value === '';

/**
 * entidad -> payload del backend.
 * Al crear (`includeEmpty: false`) se omiten los opcionales vacíos; al actualizar (`true`) se envían
 * como null para poder limpiar un valor. `uuid` solo va en la creación. `asistentes` (opcional) viaja en el mismo payload.
 */
export const toApiPayload = (capacitacion, { includeEmpty = false, asistentes } = {}) => {
  const payload = {
    nombre_capacitacion: capacitacion.nombre_capacitacion.trim(),
    responsable: capacitacion.responsable.trim(),
    fecha: toApiDate(capacitacion.fecha),
  };
  if (!includeEmpty) payload.uuid = capacitacion.uuid;
  // Lista FINAL [{ uuid, id_cuenta }] (el backend la reconcilia por uuid); si no se pasa, el backend no toca los asistentes.
  if (asistentes !== undefined) payload.asistentes = asistentes;
  OPTIONAL_FIELDS.forEach(field => {
    const raw = capacitacion[field];
    const value = typeof raw === 'string' ? raw.trim() : raw;
    if (!isEmpty(value)) payload[field] = value;
    else if (includeEmpty) payload[field] = null;
  });
  // Lista FINAL de actividades. Al crear sin actividades se omite; al actualizar se envía [] para limpiar.
  const ids = toIds(capacitacion.id_actividades);
  if (ids.length > 0 || includeEmpty) payload.id_actividades = ids;
  // id_actividad = primera actividad: compatibilidad con la app móvil y con backends que aún guardan una sola.
  if (ids.length > 0) payload.id_actividad = ids[0];
  else if (includeEmpty) payload.id_actividad = null;
  return payload;
};
